import axios from "axios";

/**
 * KLIEN HTTP KE BACKEND PLATFORM (bukan Odoo lagi).
 * ============================================================================
 * App ini sekarang STANDALONE: semua request ke backend platform kita sendiri
 * (Express + PostgreSQL). Odoo hanya sumber master data yang disync sekali.
 *
 * Fitur:
 *  - Sisipkan Bearer access token (JWT platform) ke tiap request.
 *  - Auto-refresh: kalau access token kadaluarsa (401), sekali coba tukar pakai
 *    refresh token via /auth/refresh lalu ulangi request. Kalau tetap gagal,
 *    sesi dibersihkan & event "hris:session-expired" ditembakkan (ditangkap
 *    AuthContext untuk lempar ke /login).
 *
 * Kontrak response backend:
 *   sukses → { success: true, message, data }
 *   gagal  → { success: false, error: { code, message } }
 * ============================================================================
 */

function resolveApiBaseUrl() {
  const configured = process.env.REACT_APP_PLATFORM_API_URL || "http://localhost:4000/api";
  if (typeof window === "undefined") return configured;

  try {
    const url = new URL(configured);
    const currentHost = window.location.hostname;
    const isLocalApi = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    const isLocalPage =
      currentHost === "localhost" ||
      currentHost === "127.0.0.1" ||
      /^10\./.test(currentHost) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(currentHost) ||
      /^192\.168\./.test(currentHost);

    if (isLocalApi && isLocalPage && url.hostname !== currentHost) {
      url.hostname = currentHost;
      return url.toString().replace(/\/$/, "");
    }
  } catch {
    return configured;
  }

  return configured;
}

export const API_BASE_URL = resolveApiBaseUrl();

// Access token sengaja hanya di memory. Refresh token disimpan backend sebagai
// HttpOnly cookie sehingga JavaScript tidak bisa membacanya.
const USER_KEY = "pjs_user";
const REFRESH_RETRY_MS = 30000;
let accessToken = null;
let refreshing = null;
let refreshTimer = null;
// NIK+PIN dari login OFFLINE, hanya di memory (tak pernah disimpan). Dipakai
// untuk login ulang diam-diam begitu server kembali, bila refresh cookie tak ada.
let reloginCredentials = null;

/**
 * Error yang berarti sesi BENAR-BENAR ditolak server (bukan jaringan/server
 * mati). Hanya ini yang boleh memaksa logout; error jaringan/5xx/timeout =
 * tetap login (mode offline) dan coba lagi nanti.
 */
export function isAuthRejection(err) {
  if (!err?.isAxiosError) return true; // mis. respons refresh tanpa token
  const status = err.response?.status;
  return status === 401 || status === 403;
}

export function setReloginCredentials(creds) {
  reloginCredentials = creds?.nik && creds?.pin ? { nik: creds.nik, pin: creds.pin } : null;
}

function clearRefreshTimer() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

function clearTokenState() {
  clearRefreshTimer();
  accessToken = null;
  reloginCredentials = null;
  localStorage.removeItem(USER_KEY);
}

function expireSession() {
  clearTokenState();
  window.dispatchEvent(new CustomEvent("hris:session-expired"));
}

function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function requestTokenRefresh() {
  if (!refreshing) {
    refreshing = axios
      .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true, timeout: 20000 })
      .then((r) => r.data?.data)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function reloginWithCredentials() {
  const { nik, pin } = reloginCredentials;
  const r = await axios.post(
    `${API_BASE_URL}/auth/login`,
    { username: nik, password: pin, device: "hris-web" },
    { withCredentials: true, timeout: 20000 }
  );
  const data = r.data?.data;
  if (!data?.token) throw new Error("login failed");
  reloginCredentials = null; // sudah punya sesi server; refresh cookie mengambil alih
  return data;
}

export async function refreshAccessToken() {
  let data;
  if (reloginCredentials) {
    // Sesi dari login OFFLINE: pulihkan dengan login ulang memakai NIK+PIN yang
    // diketik — BUKAN refresh cookie, yang bisa saja milik akun lain yang
    // sebelumnya logout saat offline di HP yang sama.
    data = await reloginWithCredentials();
  } else {
    data = await requestTokenRefresh();
    if (!data?.token) throw new Error("refresh failed");
  }
  tokenStore.setTokens(data);
  return data;
}

/** Refresh gagal: logout hanya bila server menolak; gagal jaringan → coba lagi nanti. */
function handleBackgroundRefreshError(err) {
  if (isAuthRejection(err)) {
    expireSession();
    return;
  }
  clearRefreshTimer();
  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(handleBackgroundRefreshError);
  }, REFRESH_RETRY_MS);
}

function scheduleAccessRefresh(token) {
  clearRefreshTimer();
  const exp = decodeJwtPayload(token)?.exp;
  if (!exp) return;
  const refreshInMs = Math.max(exp * 1000 - Date.now() - 60000, 5000);
  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(handleBackgroundRefreshError);
  }, refreshInMs);
}

export const tokenStore = {
  getAccess: () => accessToken,
  getRefresh: () => null,
  getUser: () => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  setTokens: ({ token }) => {
    if (token) {
      accessToken = token;
      scheduleAccessRefresh(token);
    }
  },
  setAccess: (token) => {
    accessToken = token;
    if (token) scheduleAccessRefresh(token);
  },
  setUser: (user) => {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    clearTokenState();
  },
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ── Request: sisipkan token ────────────────────────────────
api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Upload file (FormData): WAJIB buang default "Content-Type: application/json".
  // Kalau tidak, axios v1 justru meng-JSON-kan FormData (file jadi objek kosong)
  // → multer di backend tak dapat file → 400 FILE_REQUIRED. Dengan header dibuang,
  // browser mengisi sendiri "multipart/form-data; boundary=..." yang benar.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
    } else if (config.headers) {
      delete config.headers["Content-Type"];
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthCall = original?.url?.includes("/auth/");

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        const data = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${data.token}`;
        return api(original);
      } catch (e) {
        // Logout hanya bila server menolak sesi. Gagal jaringan saat refresh
        // (server baru mati) → tetap login; request ini gagal biasa.
        if (isAuthRejection(e)) expireSession();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

/** Ekstrak pesan error ramah dari envelope backend. */
export function errorMessage(error, fallback = "Terjadi kesalahan") {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export function errorCode(error) {
  return error?.response?.data?.error?.code || null;
}

/** Ambil langsung `data` dari envelope { success, data }. */
export async function unwrap(promise) {
  const res = await promise;
  return res.data?.data;
}

export default api;
